import type { AnyAction } from '@reduxjs/toolkit'

import keyWrapper from '~publish/helpers/keywrapper'

const PROFILE_DISCONNECTED_MODAL_DISMISSED_KEY =
  'profilesDisconnectedModalDismissed'

type ModalState = {
  showProfilesDisconnectedModal: boolean
  switchPlanModalSource: string | null
  showFirstCommentPaywallModal: boolean
  firstCommentPaywallService: string
  showCloseComposerConfirmationModal: boolean
  modalToShowLater: {
    id: string
    params: { profileId: string }
  } | null
  showTrialCollaborationModal: boolean
  page: string
  showInstagramRemindersSetupModal: boolean
  showTagsLimitUpgradeModal: boolean
  showConnectAchannelModal: boolean
  showConnectAChannelModal?: boolean
  serviceToConnect: string | null
}

export const initialState: ModalState = {
  showProfilesDisconnectedModal: false,
  switchPlanModalSource: null,
  showFirstCommentPaywallModal: false,
  firstCommentPaywallService: '',
  showCloseComposerConfirmationModal: false,
  modalToShowLater: null,
  showTrialCollaborationModal: false,
  page: 'queue',
  showInstagramRemindersSetupModal: false,
  showTagsLimitUpgradeModal: false,
  showConnectAchannelModal: false,
  serviceToConnect: null,
}

export const actionTypes = keyWrapper('MODALS', {
  SHOW_PROFILES_DISCONNECTED_MODAL: 0,
  HIDE_PROFILES_DISCONNECTED_MODAL: 0,
  SHOW_INSTAGRAM_FIRST_COMMENT_MODAL: 0,
  HIDE_INSTAGRAM_FIRST_COMMENT_MODAL: 0,
  SHOW_FIRST_COMMENT_PAYWALL_MODAL: 0,
  HIDE_FIRST_COMMENT_PAYWALL_MODAL: 0,
  SHOW_INSTAGRAM_FIRST_COMMENT_PRO_TRIAL_MODAL: 0,
  HIDE_INSTAGRAM_FIRST_COMMENT_PRO_TRIAL_MODAL: 0,
  SAVE_MODAL_TO_SHOW_LATER: 0,
  HIDE_CLOSE_COMPOSER_CONFIRMATION_MODAL: 0,
  SHOW_CLOSE_COMPOSER_CONFIRMATION_MODAL: 0,
  HIDE_CONNECT_CHANNEL_MODAL: 0,
  SHOW_TRIAL_COLLABORATION_MODAL: 0,
  HIDE_TRIAL_COLLABORATION_MODAL: 0,
  SHOW_REMINDERS_SETUP_MODAL: 0,
  HIDE_REMINDERS_SETUP_MODAL: 0,
  SHOW_TAGS_LIMIT_UPGRADE_MODAL: 0,
  HIDE_TAGS_LIMIT_UPGRADE_MODAL: 0,
  SHOW_CONNECT_A_CHANNEL_MODAL: 0,
  HIDE_CONNECT_A_CHANNEL_MODAL: 0,
})

export default (state = initialState, action: AnyAction): ModalState => {
  switch (action.type) {
    case actionTypes.SHOW_FIRST_COMMENT_PAYWALL_MODAL:
      return {
        ...state,
        firstCommentPaywallService: action.service,
        showFirstCommentPaywallModal: true,
      }
    case actionTypes.HIDE_FIRST_COMMENT_PAYWALL_MODAL:
      return {
        ...state,
        showFirstCommentPaywallModal: false,
      }
    case actionTypes.SHOW_PROFILES_DISCONNECTED_MODAL:
      return {
        ...state,
        showProfilesDisconnectedModal: true,
      }
    case actionTypes.HIDE_PROFILES_DISCONNECTED_MODAL:
      // Save dismissed status to session storage
      sessionStorage.setItem(PROFILE_DISCONNECTED_MODAL_DISMISSED_KEY, 'true')
      return {
        ...state,
        showProfilesDisconnectedModal: false,
      }
    case actionTypes.SAVE_MODAL_TO_SHOW_LATER:
      return {
        ...state,
        modalToShowLater: {
          id: action.modalId,
          params: action.params,
        },
      }
    case actionTypes.HIDE_CLOSE_COMPOSER_CONFIRMATION_MODAL:
      return {
        ...state,
        showCloseComposerConfirmationModal: false,
      }
    case actionTypes.SHOW_CLOSE_COMPOSER_CONFIRMATION_MODAL:
      return {
        ...state,
        showCloseComposerConfirmationModal: true,
        page: action.page,
      }
    case actionTypes.SHOW_TRIAL_COLLABORATION_MODAL:
      return {
        ...state,
        showTrialCollaborationModal: true,
      }
    case actionTypes.HIDE_TRIAL_COLLABORATION_MODAL:
      return {
        ...state,
        showTrialCollaborationModal: false,
      }
    case actionTypes.SHOW_TAGS_LIMIT_UPGRADE_MODAL:
      return {
        ...state,
        showTagsLimitUpgradeModal: true,
      }
    case actionTypes.HIDE_TAGS_LIMIT_UPGRADE_MODAL:
      return {
        ...state,
        showTagsLimitUpgradeModal: false,
      }
    case actionTypes.SHOW_CONNECT_A_CHANNEL_MODAL:
      return {
        ...state,
        showConnectAChannelModal: true,
        serviceToConnect: action.service,
      }
    case actionTypes.HIDE_CONNECT_A_CHANNEL_MODAL:
      return {
        ...state,
        showConnectAChannelModal: false,
        serviceToConnect: null,
      }
    default:
      return state
  }
}

export const actions = {
  showProfilesDisconnectedModal: (): {
    type: 'MODALS__SHOW_PROFILES_DISCONNECTED_MODAL'
  } => ({
    type: actionTypes.SHOW_PROFILES_DISCONNECTED_MODAL,
  }),
  showFirstCommentPaywallModal: ({
    service,
  }: {
    service: string
  }): {
    type: typeof actionTypes.SHOW_FIRST_COMMENT_PAYWALL_MODAL
    service: string
  } => ({
    type: actionTypes.SHOW_FIRST_COMMENT_PAYWALL_MODAL,
    service,
  }),
  hideInstagramFirstCommentPaywallModal: (): {
    type: 'MODALS__HIDE_FIRST_COMMENT_PAYWALL_MODAL'
  } => ({
    type: actionTypes.HIDE_FIRST_COMMENT_PAYWALL_MODAL,
  }),
  hideProfilesDisconnectedModal: (): {
    type: 'MODALS__HIDE_PROFILES_DISCONNECTED_MODAL'
  } => ({
    type: actionTypes.HIDE_PROFILES_DISCONNECTED_MODAL,
  }),
  showTrialCollaborationModal: (): {
    type: 'MODALS__SHOW_TRIAL_COLLABORATION_MODAL'
  } => ({
    type: actionTypes.SHOW_TRIAL_COLLABORATION_MODAL,
  }),
  hideTrialCollaborationModal: (): {
    type: 'MODALS__HIDE_TRIAL_COLLABORATION_MODAL'
  } => ({
    type: actionTypes.HIDE_TRIAL_COLLABORATION_MODAL,
  }),
  saveModalToShowLater: ({
    modalId,
    profileId,
  }: {
    modalId: string
    profileId: string
  }): {
    type: typeof actionTypes.SAVE_MODAL_TO_SHOW_LATER
    modalId: string
    params: { profileId: string }
  } => ({
    type: actionTypes.SAVE_MODAL_TO_SHOW_LATER,
    modalId,
    params: {
      profileId,
    },
  }),
  hideConnectChannelModal: (): {
    type: 'MODALS__HIDE_CONNECT_CHANNEL_MODAL'
  } => ({
    type: actionTypes.HIDE_CONNECT_CHANNEL_MODAL,
  }),
  isShowingModals: ({ modals }: { modals: ModalState }): boolean => {
    return (
      modals &&
      (modals.showProfilesDisconnectedModal ||
        modals.showCloseComposerConfirmationModal ||
        modals.showFirstCommentPaywallModal ||
        modals.showConnectAChannelModal ||
        false)
    )
  },
  showTagsLimitUpgradeModal: (): { type: string } => ({
    type: actionTypes.SHOW_TAGS_LIMIT_UPGRADE_MODAL,
  }),
  hideTagsLimitUpgradeModal: (): { type: string } => ({
    type: actionTypes.HIDE_TAGS_LIMIT_UPGRADE_MODAL,
  }),
  showConnectAChannelModal: (
    service: string,
  ): { type: string; service: string } => ({
    type: actionTypes.SHOW_CONNECT_A_CHANNEL_MODAL,
    service,
  }),
  hideConnectAChannelModal: (): { type: string } => ({
    type: actionTypes.HIDE_CONNECT_A_CHANNEL_MODAL,
  }),
}
